/**
 * AdminProduction.js
 * Flat item-level table. Clicking a row opens a slide-in drawer
 * showing ProductProgressStrip for that order.
 *
 * Columns: Order # | Item + Description | Qty | Delivery Date | Steps Done | Current Stage
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FiSearch, FiActivity, FiClock, FiCheckCircle,
  FiPauseCircle, FiRefreshCw, FiX,
} from "react-icons/fi";
import API from "../utils/api";
import toast from "react-hot-toast";
import ProductProgressStrip from "../components/ProductProgressStrip";
import "./SalesOrders.css";

/* ── shared exports ── */
export const PROCESS_STAGES = [
  "Design / Artwork","Customer Approval","Prepress / Plate","Printing",
  "Cutting / Trimming","Lamination","Binding / Finishing","Quality Check",
  "Packing","Ready for Dispatch","Custom…",
];
export const PROCESS_STATUS_META = {
  pending:     { label: "Pending",     tone: "gray",  icon: FiClock },
  in_progress: { label: "In Progress", tone: "blue",  icon: FiActivity },
  completed:   { label: "Completed",   tone: "green", icon: FiCheckCircle },
  on_hold:     { label: "On Hold",     tone: "red",   icon: FiPauseCircle },
};
export const StageAutocomplete = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter((o) => o.toLowerCase().includes((value || "").toLowerCase()));
  const select = (opt) => { onChange(opt); setOpen(false); };
  const onKey = (e) => {
    if (!open) { if (e.key === "ArrowDown") setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[highlight]) select(filtered[highlight]); else setOpen(false); }
    else if (e.key === "Escape") { setOpen(false); }
  };
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input type="text" value={value} placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)} onKeyDown={onKey}
        style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px",
          border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none" }}
        autoComplete="off" />
      {open && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4,
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 200, overflowY: "auto", zIndex: 20 }}>
          {filtered.map((opt, i) => (
            <div key={opt} onMouseDown={() => select(opt)} onMouseEnter={() => setHighlight(i)}
              style={{ padding: "8px 12px", fontSize: 13, cursor: "pointer",
                background: i === highlight ? "var(--primary)" : "#fff",
                color: i === highlight ? "#fff" : "#111" }}>
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── helpers ── */
const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const isPaymentTermMet = (order, activeTerm) => {
  if (!order) return false;
  if (order.paymentType === "credit") return true;
  if (!activeTerm) return true;
  const grandTotal     = Number(order.grandTotal)     || 0;
  const amountReceived = Number(order.amountReceived) || 0;
  const paidPercent    = grandTotal > 0 ? (amountReceived / grandTotal) * 100 : 100;
  const threshold  = activeTerm.threshold  != null ? Number(activeTerm.threshold)  : null;
  const minAmount  = activeTerm.minAmount  != null ? Number(activeTerm.minAmount)  : null;
  const flatAmount = activeTerm.flatAmount != null ? Number(activeTerm.flatAmount) : null;
  if (minAmount !== null && grandTotal <= minAmount) return paidPercent >= 100;
  const percentRequired = threshold !== null ? (threshold / 100) * grandTotal : 0;
  const requiredAmount  = flatAmount !== null ? Math.max(flatAmount, percentRequired) : percentRequired;
  if (threshold === null) return true;
  return amountReceived >= requiredAmount;
};

const buildRows = (order) => {
  const items          = Array.isArray(order.items)          ? order.items          : [];
  const processEntries = Array.isArray(order.processEntries) ? order.processEntries : [];
  const readyEntries   = Array.isArray(order.readyEntries)   ? order.readyEntries   : [];
  const stepsCompleted = order.stepsCompleted || {};
  const isDelivered    = order.status === "delivered";

  return items
    .map((line) => {
      const orderedQty  = Number(line.qty) || 0;
      const readyQty    = readyEntries
        .filter((e) => e.itemLineId === line.id)
        .reduce((sum, e) => sum + (Number(e.qty) || 0), 0);
      const itemFullyReady = readyQty >= orderedQty && orderedQty > 0;

      const lineSteps = processEntries.filter((e) => e.productLineId === line.id);
      const steps = [
        { id: "__print", label: "Print", done: true },
        ...lineSteps.map((s) => ({ id: s.id, label: s.stage, done: !!stepsCompleted[s.id] })),
        { id: "__ready", label: "Ready", done: isDelivered || readyQty >= orderedQty },
      ];
      const doneCount   = steps.filter((s) => s.done).length;
      const currentStep = steps.find((s) => !s.done) || steps[steps.length - 1];

      return {
        orderId:       order.id,
        orderNumber:   order.orderNumber,
        customerName:  order.customerName  || "",
        companyName:   order.companyName   || "",
        customerPhone: order.customerPhone || "",
        deliveryDate:  order.deliveryDate || null,
        lineId:        line.id,
        item:          line.product     || "—",
        description:   line.description || "",
        qty:           `${line.qty || ""} ${line.unit || ""}`.trim(),
        orderedQty,
        readyQty,
        unit:          line.unit || "",
        itemFullyReady,
        stepsDone:     doneCount,
        stepsTotal:    steps.length,
        currentStage:  currentStep.label,
        allDone:       doneCount === steps.length,
      };
    })
    // Hide fully ready items — only items still in production remain visible
    .filter((row) => !row.itemFullyReady);
};

/* ── Step progress pill ── */
const StepsPill = ({ done, total }) => {
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = done === total ? "#059669" : done > 2 ? "#2563eb" : "#f59e0b";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 52, height: 5, borderRadius: 99, background: "#e5e7eb", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: "0.78rem", fontWeight: 700, color }}>{done}/{total}</span>
    </div>
  );
};

/* ── Stage badge ── */
const StageBadge = ({ label, allDone }) => (
  <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 100,
    fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    background: allDone ? "#d1fae5" : "#eff6ff",
    color:      allDone ? "#059669" : "#2563eb" }}>
    {label}
  </span>
);

/* ── Production Drawer ── */
const ProductionDrawer = ({ order, onClose, onOrderUpdate }) => {
  // close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9000 }}
      />
      {/* Drawer panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 560, maxWidth: "95vw",
        background: "var(--bg-soft, #f9fafb)",
        zIndex: 9001, display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.14)",
        animation: "slideInDrawer 0.22s ease",
      }}>
        <style>{`@keyframes slideInDrawer { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>

        {/* Drawer header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", background: "#fff",
          borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111" }}>
              {order.orderNumber}
            </div>
            <div style={{ fontSize: "0.78rem", color: "#9ca3af", marginTop: 2 }}>
              {order.customerName}
              {order.items?.[0]?.product && (
                <span style={{ color: "#374151", fontWeight: 600 }}> · {order.items[0].product}</span>
              )}
              {order.deliveryDate && (
                <span> · Delivery {fmtDate(order.deliveryDate)}</span>
              )}
            </div>
          </div>
          <button onClick={onClose}
            style={{ border: "none", background: "none", cursor: "pointer",
              color: "#9ca3af", display: "flex", padding: 4, borderRadius: 6 }}>
            <FiX size={18} />
          </button>
        </div>

        {/* Drawer body — ProductProgressStrip */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          <ProductProgressStrip order={order} onOrderUpdate={onOrderUpdate} />
        </div>
      </div>
    </>
  );
};

/* ── Main component ── */
const AdminProduction = () => {
  const [orders, setOrders]           = useState([]);
  const [activeTerm, setActiveTerm]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [orderFrom, setOrderFrom]     = useState("");
  const [orderTo, setOrderTo]         = useState("");
  const [deliveryFrom, setDeliveryFrom] = useState("");
  const [deliveryTo, setDeliveryTo]   = useState("");
  const [drawer, setDrawer]           = useState(null);

  const loadAll = useCallback(async (q = "") => {
    setLoading(true);
    try {
      const [ordersRes, termRes] = await Promise.all([
        API.get(`/sales-orders${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        API.get("/payment-terms/active").catch(() => ({ data: { term: null } })),
      ]);
      const all = ordersRes.data.orders || [];
      setOrders(all.filter((o) => o.status === "confirmed" && isPaymentTermMet(o, termRes.data.term || null)));
      setActiveTerm(termRes.data.term || null);
    } catch {
      toast.error("Could not load production data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const t = setTimeout(() => loadAll(search), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openDrawer = useCallback(async (orderId, lineId) => {
    const cached = orders.find((o) => o.id === orderId);
    if (cached) setDrawer({ order: cached, lineId });
    try {
      const { data } = await API.get(`/sales-orders/${orderId}`);
      const fresh = data.order || data;
      if (fresh) setDrawer({ order: fresh, lineId });
    } catch {
      if (!cached) setDrawer(null);
    }
  }, [orders]);

  const handleOrderUpdate = useCallback((updated) => {
    setDrawer((prev) => prev ? { ...prev, order: updated } : null);
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
  }, []);

  const toISO = (d) => { if (!d) return ""; return new Date(d).toISOString().slice(0, 10); };

  const rows = orders.flatMap((o) => buildRows(o));
  const filtered = rows.filter((r) => {
    if (search && !(
      r.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
      (r.customerName || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.companyName || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.customerPhone || "").toLowerCase().includes(search.toLowerCase()) ||
      r.item.toLowerCase().includes(search.toLowerCase()) ||
      (r.description || "").toLowerCase().includes(search.toLowerCase())
    )) return false;
    if (filterStage === "in_progress" && r.allDone) return false;
    if (filterStage === "completed"   && !r.allDone) return false;
    const od = toISO(orders.find(o => o.id === r.orderId)?.orderDate);
    if (orderFrom && od < orderFrom) return false;
    if (orderTo   && od > orderTo)   return false;
    const dd = toISO(r.deliveryDate);
    if (deliveryFrom && dd < deliveryFrom) return false;
    if (deliveryTo   && dd > deliveryTo)   return false;
    return true;
  });

  const hasFilter = filterStage !== "all" || orderFrom || orderTo || deliveryFrom || deliveryTo;

  const COLS = ["Order #", "Customer", "Item", "Qty", "Qty Ready", "Delivery Date", "Steps Done", "Current Stage"];

  return (
    <div className="so-page">
      {/* Header */}
      <div className="so-page-head">
        <div>
          <h2>Production</h2>          
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="so-filter-bar">
        {/* Search */}
        <div className="so-search-input so-search-input--filter">
          <FiSearch size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order number or item…"
          />
          <button onClick={() => loadAll(search)} title="Refresh"
            style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", display: "flex", padding: 2 }}>
            <FiRefreshCw size={14} />
          </button>
        </div>

        {/* Order date range */}
        <div className="so-filter-dates">
          <span className="so-date-label">Order</span>
          <input type="date" className="so-date-input" title="Order from"
            value={orderFrom} onChange={(e) => setOrderFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
          <span className="so-date-sep">–</span>
          <input type="date" className="so-date-input" title="Order to"
            value={orderTo} onChange={(e) => setOrderTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
        </div>

        {/* Delivery date range */}
        <div className="so-filter-dates">
          <span className="so-date-label">Delivery</span>
          <input type="date" className="so-date-input" title="Delivery from"
            value={deliveryFrom} onChange={(e) => setDeliveryFrom(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
          <span className="so-date-sep">–</span>
          <input type="date" className="so-date-input" title="Delivery to"
            value={deliveryTo} onChange={(e) => setDeliveryTo(e.target.value)}
            onClick={(e) => e.target.showPicker && e.target.showPicker()} />
        </div>

        {/* Stage pills */}
        <div className="so-pill-group">
          {[["all","All"],["in_progress","In Progress"],["completed","Completed"]].map(([val, label]) => (
            <button key={val}
              className={`so-pill${filterStage === val ? " so-pill--active" : ""}`}
              onClick={() => setFilterStage(val)}>{label}</button>
          ))}
        </div>

        {hasFilter && (
          <button className="so-clear-btn" onClick={() => {
            setFilterStage("all");
            setOrderFrom(""); setOrderTo("");
            setDeliveryFrom(""); setDeliveryTo("");
          }}>Clear</button>
        )}
      </div>

      {/* ── Table card ── */}
      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="so-empty">
            {search || hasFilter ? "No matching items." : "No confirmed orders yet."}
          </div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  {COLS.map((h) => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={`${row.orderId}-${row.lineId}`}
                    onClick={() => openDrawer(row.orderId, row.lineId)}
                  >
                    <td className="so-strong" style={{ color: "var(--primary)", whiteSpace: "nowrap" }}>
                      {row.orderNumber}
                    </td>
                    <td>
                      {(() => {
                        const o = orders.find((o) => o.id === row.orderId) || {};
                        return (
                          <>
                            <div className="so-strong">{o.companyName || o.customerName}</div>
                            {o.companyName && <div className="so-muted">{o.customerName}</div>}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <div className="so-strong" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {row.item}
                      </div>
                      {row.description && (
                        <div className="so-muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.description}
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{row.qty}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {row.readyQty > 0 ? (
                        <span className="so-badge so-badge--blue" style={{ background: "#fef3c7", color: "#d97706" }}>
                          {row.readyQty}/{row.orderedQty} {row.unit} ready
                        </span>
                      ) : (
                        <span className="so-muted">—</span>
                      )}
                    </td>
                    <td className="so-muted" style={{ whiteSpace: "nowrap" }}>{fmtDate(row.deliveryDate)}</td>
                    <td><StepsPill done={row.stepsDone} total={row.stepsTotal} /></td>
                    <td><StageBadge label={row.currentStage} allDone={row.allDone} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ padding: "8px 14px", borderTop: "1px solid #f3f4f6",
            fontSize: "0.7rem", color: "#b0b7c3", background: "#fafafa" }}>
            {filtered.length} item{filtered.length !== 1 ? "s" : ""} across {orders.length} order{orders.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>

      {/* Drawer */}
      {drawer && drawer.order && (() => {
        const singleItemOrder = {
          ...drawer.order,
          items: (drawer.order.items || []).filter((i) => i.id === drawer.lineId),
        };
        return (
          <ProductionDrawer
            order={singleItemOrder}
            onClose={() => setDrawer(null)}
            onOrderUpdate={handleOrderUpdate}
          />
        );
      })()}
    </div>
  ); // so-page
};

export default AdminProduction;